// Kalendář tréninků (vlastní rezervační systém, 2. 9. 2026).
//
// Týdenní mřížka Po až Ne, celé hodiny 6 až 21. Zobrazuje rezervace ze všech
// zdrojů (Reservio i vlastní), schválené absence a svátky. Klik na volnou
// hodinu = nová rezervace (jednorázová nebo každý týden), klik na trénink =
// detail s přesunem, zrušením (včas nebo propadá), přidáním druhé klientky
// (spojený trénink). Mobil: jeden den, přepínání dnů nahoře.
//
// 15. 10. 2026: samotná stránka kalendáře je v KalendarTrenera.jsx, tady zůstávají
// sdílené modaly (nová rezervace, detail), panely a pomocné funkce.
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, CalendarDays, CalendarPlus, CheckCircle2, ChevronDown, Clock, Info, Lock, MapPin, Plus, Repeat, Smartphone, Timer, TimerOff, Users, X, XCircle, Copy, Check } from "lucide-react";
import { apiFetch, useApiData } from "../../lib/api.js";
import { getCurrentUser } from "../../lib/auth.js";
import { confirmDialog } from "../../components/confirmHost.jsx";
import ClientAutocomplete from "../../components/ClientAutocomplete.jsx";
import { toast } from "../../components/Toast.jsx";
import { Zapinatko } from "../../components/ui.jsx";
import KartaZkusebniTrener from "../../components/sales/KartaZkusebniTrener.jsx";
import { RozhodnutiZkusebni, DialogZkusebni, BlokPodepsat } from "../../components/deal/DealDialogy.jsx";

const DNY_KRATCE = ["", "Po", "Út", "St", "Čt", "Pá", "So", "Ne"];
const DNY_DLOUHE = ["", "Pondělí", "Úterý", "Středa", "Čtvrtek", "Pátek", "Sobota", "Neděle"];
// Lokality přijdou z API (tabulka lokality, jen aktivní); tohle je jen záloha do načtení.
const LOKALITY_VYCHOZI = [{ kod: "VT", nazev: "VT GYM" }];
function lokalityZ(data) { return data?.lokality?.length ? data.lokality : LOKALITY_VYCHOZI; }
function nazevLokality(data, kod) { return lokalityZ(data).find((l) => l.kod === kod)?.nazev || kod || "?"; }

function pragueDate() { return new Date(new Date().toLocaleString("en-US", { timeZone: "Europe/Prague" })); }
function isoOf(d) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; }
function addDays(iso, n) { const [y, m, d] = iso.split("-").map(Number); return new Date(Date.UTC(y, m - 1, d + n)).toISOString().substring(0, 10); }
function fmtCz(iso) { if (!iso) return ""; const [y, m, d] = iso.split("-").map(Number); return `${d}. ${m}.`; }
function fmtCzRok(iso) { if (!iso) return ""; const [y, m, d] = iso.split("-").map(Number); return `${d}. ${m}. ${y}`; }
function casMin(cas) { const m = String(cas || "").match(/^(\d{1,2}):(\d{2})/); return m ? parseInt(m[1], 10) * 60 + parseInt(m[2], 10) : null; }
function hh(h) { return `${String(h).padStart(2, "0")}:00`; }
// Typ podle služby (29. 9. 2026: všechny typy jsou u nás, z Reservia zůstávají jen do přepnutí ke čtení).
function jeZkusebni(s) { return /zku[sš]eb|nutri/i.test(String(s || "")); }
function jeNutri(s) { return /nutri/i.test(String(s || "")); }
function jeSkoleni(s) { return /[sš]kolen/i.test(String(s || "")); }
export const TYPY_REZERVACE = {
  // 30. 9. 2026 (Láďa): v aplikaci celé názvy. Trénink, Spojený trénink (vzniká sám ze dvou), Zkušební trénink, Nutriční konzultace.
  osobni:   { nazev: "Trénink",              kratce: "Trénink",             delka: 60, klientka: true,  krok: 60 },
  zkusebni: { nazev: "Zkušební trénink",     kratce: "Zkušební trénink",    delka: 90, klientka: false, krok: 30 },
  nutricni: { nazev: "Nutriční konzultace",  kratce: "Nutriční konzultace", delka: 60, klientka: true,  krok: 60 },
  skoleni:  { nazev: "Školení nového trenéra", kratce: "Školení",           delka: 60, klientka: false, krok: 60 },
  // Volno trenéra (29. 9. 2026): není rezervace, ale zadává se stejným oknem. Blokuje
  // hodiny pro klientky, ukládá se do rezervace_volno (bez schvalování, mimo HR absence).
  volno:    { nazev: "Volno trenéra",         kratce: "Volno",    delka: 60, klientka: false, krok: 60, volno: true },
};
export function typRezervace(r) { return r?.typ && TYPY_REZERVACE[r.typ] ? r.typ : jeNutri(r?.sluzba) ? "nutricni" : jeZkusebni(r?.sluzba) ? "zkusebni" : jeSkoleni(r?.sluzba) ? "skoleni" : "osobni"; }
// Kdo smí zakládat jaký typ: sales jen zkušební, nutriční jen konzultace, ostatní vše kromě konzultací (ty má nutriční).
// Kdo smí zakládat jaký typ (29. 9. 2026): trenér jen osobní trénink a své volno, nutriční jen konzultace
// a volno, sales jen zkušební (zájemkyně z leadů nebo vracející se klientka), owner/manager/CSM osobní,
// zkušební, školení a volno trenéra. Konzultaci zadává jen nutriční. Server to hlídá taky.
export function povoleneTypy(user, trener) {
  const role = String(user?.role || "").toLowerCase();
  if (role === "sales") return ["zkusebni"];
  if (["nutritionist", "nutricni"].includes(role)) return ["nutricni", "volno"];
  if (trener?.nutricni) return ["volno"]; // v kalendáři nutriční zadá manažer jen její volno, konzultace jen ona
  if (["trener", "head_coach"].includes(role)) return ["osobni", "volno"];
  return ["osobni", "zkusebni", "skoleni", "volno"];
}


async function post(body) {
  const r = await apiFetch("/api/rezervace", { method: "POST", body: JSON.stringify(body) });
  let j = {};
  try { j = await r.json(); } catch { /* prázdná odpověď */ }
  if (!r.ok) {
    const e = new Error(j.error || `Chyba ${r.status}`);
    e.kod = j.kod || "";       // server umí požádat o potvrzení místo tvrdé chyby
    e.detail = j.detail || null;
    e.status = r.status;
    throw e;
  }
  return j;
}

// Odkaz na profil klientky: trenér má vlastní seznam (/trener/klienti), ostatní Klienti.
function profilKlientky(user, jmeno) {
  const role = String(user?.role || "").toLowerCase();
  return role === "trener" || role === "head_coach" ? `/trener/klienti?q=${encodeURIComponent(jmeno || "")}` : `/clients?search=${encodeURIComponent(jmeno || "")}&open=1`;
}

// 15. 10. 2026: starý tabulkový kalendář (TrenerKalendar, ManagerKalendar, KalendarTrenera)
// smazán. Od 29. 9. se používá KalendarTrenera.jsx (mřížka jako Kalendář týmu), tenhle
// soubor dál drží sdílené části: modaly rezervace, panely a pomocné funkce.
export { Chip, seskupit, SeriePanel, HodinyPanel, IcalPanel, post as postRezervace };

function seskupit(rez) {
  const map = new Map();
  for (const r of rez) {
    const k = r.cas || r.booking_id;
    if (!map.has(k)) map.set(k, { klic: `${k}-${r.booking_id}`, cas: r.cas, cas_konec: r.cas_konec, polozky: [] });
    map.get(k).polozky.push(r);
  }
  return [...map.values()].sort((a, b) => (casMin(a.cas) ?? 0) - (casMin(b.cas) ?? 0));
}

function Chip({ skupina, onClick, user }) {
  const { polozky } = skupina;
  const zkusebni = polozky.some((r) => typRezervace(r) !== "osobni");
  const spojeny = polozky.length > 1;
  const vsePropadle = polozky.every((r) => r.propadnuti === "ano");
  const t0 = typRezervace(polozky[0]);
  // 29. 9. 2026 (Láďa): osobní fialová, spojený zelená, nutriční žlutá, zkušební červená,
  // školení šedá; propadlý šedý s přeškrtnutým jménem.
  const pal = vsePropadle ? { box: "bg-slate-200/70 border-slate-300", time: "text-slate-500", label: "text-rose-700", name: "text-slate-500" }
    : t0 === "nutricni" ? { box: "bg-amber-100 border-amber-300", time: "text-amber-700", label: "text-amber-800", name: "text-amber-950" }
    : t0 === "skoleni" ? { box: "bg-slate-100 border-slate-300", time: "text-slate-600", label: "text-slate-700", name: "text-slate-900" }
    : zkusebni ? { box: "bg-rose-100 border-rose-300", time: "text-rose-700", label: "text-rose-800", name: "text-rose-950" }
    : spojeny ? { box: "bg-emerald-100 border-emerald-400 border-2", time: "text-emerald-700", label: "text-emerald-800", name: "text-emerald-950" }
    : { box: "bg-brand-100 border-brand-200", time: "text-brand-700", label: "text-brand-800", name: "text-brand-900" };
  // 6. 10. 2026 (Láďa, schválený mockup): rezervace z aplikace jsou poznat na první pohled.
  // Čekající na potvrzení = šrafovaná žlutá s přerušovaným okrajem, potvrzená z aplikace
  // = zelený proužek vlevo a štítek „aplikace“. Zapsaná trenérem beze změny.
  const ceka = polozky.some((r) => r.status === "ceka");
  const zAplikace = !ceka && polozky.some((r) => /^portal:/.test(r.created_by || ""));
  const typ = ceka ? "Potvrdit" : (vsePropadle ? "Propadl, " : "") + (typRezervace(polozky[0]) !== "osobni" ? TYPY_REZERVACE[typRezervace(polozky[0])].kratce : spojeny ? `Spojený trénink (${polozky.length})` : "Trénink");
  const box = ceka ? "border border-dashed border-amber-500 bg-amber-50 bg-[repeating-linear-gradient(135deg,#FEF3C7_0_6px,#FDE68A_6px_8px)]" : pal.box;
  const casBarva = ceka ? "text-amber-800" : pal.time;
  return (
    <div className={`h-full rounded-xl border p-1.5 overflow-hidden flex flex-col cursor-pointer ${box} ${zAplikace ? "border-l-4 !border-l-emerald-500" : ""}`} onClick={() => onClick(polozky[0])} title={ceka ? "Čeká na potvrzení, klientka rezervovala v aplikaci" : zAplikace ? "Rezervovala klientka v aplikaci, potvrzeno" : "Detail tréninku"}>
      <div className="flex items-baseline gap-1.5 whitespace-nowrap">
        <div className={`text-[11px] font-black tabular-nums flex-shrink-0 ${casBarva}`}>{skupina.cas}{skupina.cas_konec ? `–${skupina.cas_konec}` : ""}</div>
        <div className={`text-[11px] font-bold uppercase tracking-wider truncate ${ceka ? "text-amber-800" : pal.label}`}>{typ}</div>
      </div>
      <div className="mt-0.5 flex flex-wrap gap-x-3 md:flex-col md:gap-x-0">
        {polozky.map((r) => (
          <div key={r.booking_id} className="flex items-center gap-1 min-w-0">
            <span className={`flex-shrink-0 ${pal.time}`}>•</span>
            <Link to={profilKlientky(user, r.klient_name)} onClick={(e) => e.stopPropagation()} className={`text-xs md:text-[11px] font-bold truncate hover:underline demo-sensitive ${pal.name} ${r.propadnuti === "ano" ? "line-through opacity-70" : ""}`} title={`Profil klientky: ${r.klient_name}`}>
              {r.klient_name}
            </Link>
            {r.serie_id ? <Repeat className="inline w-3 h-3 opacity-60 flex-shrink-0" /> : null}
          </div>
        ))}
      </div>
      {ceka && <span className="mt-auto inline-flex w-fit items-center gap-1 rounded-full bg-amber-500 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white"><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" />čeká na potvrzení</span>}
      {zAplikace && <span className="mt-auto inline-flex w-fit items-center gap-1 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-800"><Smartphone className="h-2.5 w-2.5" />aplikace<CheckCircle2 className="h-2.5 w-2.5" /></span>}
    </div>
  );
}

// Lišta nad kalendářem: rezervace z aplikace, které čekají na potvrzení (i mimo zobrazený týden).

// 14. 9. 2026 (fáze 6): pole formulářů a tlačítka jdou přes třídy z index.css
// (`field`, `btn`, `vyber`), místní konstanty POLE a TLACITKO_* jsou pryč.
const POPISEK = "block text-xs font-semibold text-slate-600 mb-1";

// Pobočka je vždy podle klientky (3. 9. 2026), v rezervaci se nemění. Pole ukazuje název a zámek,
// po klepnutí se pod ním objeví vysvětlení (další klepnutí ho schová). Štítek v poli překrýval název (2. 10. 2026).
function PobockaPole({ nazev, vMrizce = false }) {
  const [info, setInfo] = useState(false);
  return (
    <div className={vMrizce ? "contents" : ""}>
      <button type="button" onClick={() => setInfo((v) => !v)} aria-expanded={info} className={`field flex w-full items-center gap-2 bg-slate-50 text-left ${info ? "border-brand" : ""}`}>
        <MapPin className="h-4 w-4 shrink-0 text-brand-600" />
        <span className="min-w-0 flex-1 truncate text-sm font-bold text-slate-700">{nazev}</span>
        <Lock className="h-4 w-4 shrink-0 text-slate-400" />
      </button>
      {info && <div className={`${vMrizce ? "col-span-2" : "mt-1.5"} flex items-start gap-1.5 rounded-xl bg-brand-50 px-2.5 py-2 text-xs text-slate-600`}><Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-600" />Pobočka je podle klientky, tady se nemění. Změní se v kartě klientky.</div>}
    </div>
  );
}

// Datum: šipky den zpět a vpřed (na telefonu rychlejší než kalendářík) + nativní pole.
// 14. 9. 2026 (fáze 6): pole vypadá jako výběr (.vyber), klepnutím se otevře kalendářík.
function DatumPole({ value, onChange, disabled, min }) {
  // 29. 9. 2026: vlastní kalendářík místo nativního pole. Safari na Macu žádný
  // kalendář nenabízí (jen přepis číslic), a to se tým přepisoval. Kalendář se
  // rozbalí pod polem na celou šířku okna, dny před `min` (výchozí dnes) nejdou vybrat.
  const [otevreno, setOtevreno] = useState(false);
  const dnes = isoOf(pragueDate());
  const spodni = min || dnes;
  const [mesic, setMesic] = useState(() => (value || dnes).substring(0, 7));
  useEffect(() => { if (value) setMesic(value.substring(0, 7)); }, [value]);
  // 2. 10. 2026: u prázdného pole (Do dne u volna) šipka vpřed začne od spodní hranice.
  const posun = (n) => { if (value) { const v = addDays(value, n); if (v >= spodni) onChange(v); } else if (n > 0) onChange(spodni); };
  const [y, m] = mesic.split("-").map(Number);
  const prvni = new Date(Date.UTC(y, m - 1, 1));
  const dnu = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const offset = (prvni.getUTCDay() + 6) % 7;
  const bunky = [];
  for (let i = 0; i < offset; i++) bunky.push(null);
  for (let d = 1; d <= dnu; d++) bunky.push(`${mesic}-${String(d).padStart(2, "0")}`);
  const posunMesic = (n) => { const dd = new Date(Date.UTC(y, m - 1 + n, 1)); setMesic(dd.toISOString().substring(0, 7)); };
  const MESICE = ["leden", "únor", "březen", "duben", "květen", "červen", "červenec", "srpen", "září", "říjen", "listopad", "prosinec"];
  return (
    <div>
      <div className="flex items-stretch gap-1.5">
        <button type="button" onClick={() => posun(-1)} disabled={disabled || !value || value <= spodni} className="w-12 md:w-9 flex-shrink-0 rounded-xl border border-slate-300 bg-white text-slate-600 flex items-center justify-center active:bg-slate-100 disabled:opacity-40" aria-label="Předchozí den"><ArrowRight className="w-4 h-4 rotate-180" /></button>
        <button type="button" onClick={() => !disabled && setOtevreno((o) => !o)} aria-expanded={otevreno} aria-disabled={disabled ? "true" : undefined} className={`vyber min-w-0 flex-1 text-left ${otevreno ? "border-brand ring-4 ring-brand/10" : ""}`}>
          <CalendarDays />
          <span className="min-w-0 flex-1 truncate">{value ? `${DNY_KRATCE[dowOfIso(value)]} ${fmtCzRok(value)}` : "Vyber datum"}</span>
          <span className="vyber-sipka"><ChevronDown className={otevreno ? "rotate-180" : ""} /></span>
        </button>
        <button type="button" onClick={() => posun(1)} disabled={disabled} className="w-12 md:w-9 flex-shrink-0 rounded-xl border border-slate-300 bg-white text-slate-600 flex items-center justify-center active:bg-slate-100 disabled:opacity-40" aria-label="Další den"><ArrowRight className="w-4 h-4" /></button>
      </div>
      {otevreno && !disabled && (
        <div className="mt-2 rounded-xl border border-slate-200 bg-white p-3 shadow-glass">
          <div className="mb-2 flex items-center justify-between">
            <button type="button" onClick={() => posunMesic(-1)} className="btn-ikona !h-9 !w-9" aria-label="Předchozí měsíc"><ArrowRight className="w-4 h-4 rotate-180" /></button>
            <span className="text-sm font-bold text-slate-900">{MESICE[m - 1]} {y}</span>
            <button type="button" onClick={() => posunMesic(1)} className="btn-ikona !h-9 !w-9" aria-label="Další měsíc"><ArrowRight className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-7 gap-0.5 text-center">
            {[1, 2, 3, 4, 5, 6, 7].map((d) => <span key={d} className="py-1 text-[11px] font-bold uppercase text-slate-400">{DNY_KRATCE[d]}</span>)}
            {bunky.map((d, i) => {
              if (!d) return <span key={`p${i}`} />;
              const vybrano = d === value, minule = d < spodni, jeDnes = d === dnes;
              return <button key={d} type="button" disabled={minule} onClick={() => { onChange(d); setOtevreno(false); }} className={`flex h-11 w-full items-center justify-center rounded-lg text-[15px] tabular-nums sm:h-10 ${vybrano ? "bg-brand font-bold text-white" : minule ? "text-slate-300" : "text-slate-800 hover:bg-brand-light"} ${jeDnes && !vybrano ? "ring-1 ring-brand" : ""}`}>{Number(d.substring(8))}</button>;
            })}
          </div>
        </div>
      )}
    </div>
  );
}
function dowOfIso(iso) { const [y, m, d] = iso.split("-").map(Number); const w = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); return w === 0 ? 7 : w; }
// Čas: u dnešního data jen hodiny, které ještě nezačaly (přesun i nová rezervace nesmí do minulosti).
function jenBudouci(datum, casy) {
  const dnes = isoOf(pragueDate());
  if (!datum || datum > dnes) return casy;
  if (datum < dnes) return [];
  const n = pragueDate(); const ted = n.getHours() * 60 + n.getMinutes();
  return casy.filter((c) => (casMin(typeof c === "number" ? hh(c) : c) ?? 0) + 60 > ted);
}

function PatkaOkna({ children }) {
  return <div className="patka-okna patka-dole sticky bottom-0 -mx-4 sm:-mx-5 -mb-4 sm:-mb-5 px-4 sm:px-5 pt-3 sm:pb-5 bg-white border-t border-slate-100">{children}</div>;
}

export function NovaRezervaceModal({ init, data, trener, onClose, onDone }) {
  const klienti = data?.klienti || [];
  const [jenMoje, setJenMoje] = useState(klienti.some((k) => k.moje));
  const user = getCurrentUser();
  const typy = povoleneTypy(user, data?.trener);
  const [typ, setTyp] = useState(init.typ && typy.includes(init.typ) ? init.typ : typy[0]);
  const T = TYPY_REZERVACE[typ];
  const [jmenoVolne, setJmenoVolne] = useState(""); // zkušební (zájemkyně) a školení (nový trenér) bez klientky v Notionu
  const [telefon, setTelefon] = useState("");
  const [email, setEmail] = useState("");
  // Zkušební trénink: zájemkyně z leadů, nebo existující klientka (vrací se po delší době).
  // Nové jméno se založí jako lead, proto musí mít celé jméno, telefon i e-mail.
  const leady = data?.leady || [];
  const zajemkyne = useMemo(() => {
    const out = [];
    for (const l of leady) out.push({ klic: `lead:${l.id}`, label: `${l.jmeno}${l.stage ? ` (${l.stage})` : ""}`, jmeno: l.jmeno, telefon: l.telefon, email: l.email, lokalita: l.lokalita, lead: true, lead_id: l.id });
    for (const k of klienti) out.push({ klic: `klient:${k.notion_id}`, label: `${k.klient_name} (klientka${k.stav ? `, ${k.stav}` : ""})`, jmeno: k.klient_name, telefon: k.telefon, lokalita: k.lokalita, notion_id: k.notion_id, lead: false });
    return out;
  }, [leady, klienti]);
  const vybranaZajemkyne = typ === "zkusebni" ? zajemkyne.find((z) => z.label === jmenoVolne) || null : null;
  const [jmeno, setJmeno] = useState("");
  const [datum, setDatum] = useState(init.datum || "");
  const [cas, setCas] = useState(init.cas || "");
  const [lokalita, setLokalita] = useState(init.spojitS?.lokalita || data?.trener?.lokalita || lokalityZ(data)[0].kod);
  const [delka, setDelka] = useState(T.delka);
  useEffect(() => { setDelka(TYPY_REZERVACE[typ].delka); if (typ !== "osobni") setOpakovat(false); }, [typ]); // eslint-disable-line react-hooks/exhaustive-deps
  const [poznamka, setPoznamka] = useState("");
  const [opakovat, setOpakovat] = useState(!!init.opakovat);
  const [platnostDo, setPlatnostDo] = useState("");
  const [busy, setBusy] = useState(false);
  const [chyba, setChyba] = useState("");

  const seznam = useMemo(() => {
    const zdroj = jenMoje ? klienti.filter((k) => k.moje) : klienti;
    const pocty = new Map();
    for (const k of zdroj) pocty.set(k.klient_name, (pocty.get(k.klient_name) || 0) + 1);
    return zdroj.map((k) => ({ ...k, label: k.dite ? `${k.klient_name}${k.jmena_deti ? `, ${k.jmena_deti}` : ""} (dětská permice${k.stav_treninku != null ? `, zbývá ${k.stav_treninku}` : ""})` : pocty.get(k.klient_name) > 1 ? `${k.klient_name} (${k.lokalita || k.lokalita_text || "?"})` : k.klient_name }));
  }, [klienti, jenMoje]);
  const vybrana = seznam.find((k) => k.label === jmeno) || null;
  useEffect(() => { if (vybrana?.lokalita && !init.spojitS) setLokalita(vybrana.lokalita); }, [vybrana]); // eslint-disable-line react-hooks/exhaustive-deps

  // Volno (2. 10. 2026, zpětná vazba trenérů): od a do v celých hodinách místo času a délky,
  // rychlé volby celý den / dopoledne / odpoledne a volitelně do dne (víc dní najednou).
  // Rychlé volby: dopoledne 8 až 12, odpoledne 15 až 20, celý den 8 až 20. Výběr Od/Do umí celý rozsah dne (hod_od až hod_do), kdo trénuje déle, přepíše si to.
  const hodOd = data?.hod_od ?? 6, hodDo = data?.hod_do ?? 22;
  const RYCHLE = [["den", "Celý den", 8, 20], ["dop", "Dopoledne", 8, 12], ["odp", "Odpoledne", 15, 20]];
  const [volnoOd, setVolnoOd] = useState(init.cas ? Math.floor((casMin(init.cas) ?? 0) / 60) : 8);
  const [volnoDo, setVolnoDo] = useState(init.cas ? Math.min(hodDo, Math.floor((casMin(init.cas) ?? 0) / 60) + 1) : 20);
  const [volnoDoDne, setVolnoDoDne] = useState("");
  const volnoDny = useMemo(() => { if (!datum) return []; const out = []; let d = datum; const konec = volnoDoDne && volnoDoDne > datum ? volnoDoDne : datum; while (d <= konec && out.length < 31) { out.push(d); d = addDays(d, 1); } return out; }, [datum, volnoDoDne]);
  const hodinyCele = []; for (let h = hodOd; h <= hodDo; h++) hodinyCele.push(h);
  const volnoRychle = RYCHLE.find(([, , o, d]) => volnoOd === o && volnoDo === d)?.[0] || "";

  const odeslat = async () => {
    setChyba("");
    if (T.volno) {
      if (!datum) return setChyba("Vyplň datum.");
      if (volnoDo <= volnoOd) return setChyba("Konec volna musí být po začátku.");
      setBusy(true);
      try {
        for (const d of volnoDny) await post({ akce: "volno_pridat", trener, datum: d, od_hod: volnoOd, do_hod: volnoDo, duvod: poznamka.trim() || "volno" });
        toast(volnoDny.length > 1 ? `Volno uloženo na ${volnoDny.length} dny, hodiny se klientkám nenabízejí.` : "Volno uloženo, hodiny se klientkám nenabízejí.", "success"); onDone();
      } catch (e) { setChyba(e.message); } finally { setBusy(false); }
      return;
    }
    if (T.klientka && !vybrana) return setChyba("Vyber klientku ze seznamu.");
    if (!T.klientka && !jmenoVolne.trim()) return setChyba(typ === "skoleni" ? "Napiš jméno školeného trenéra." : "Napiš jméno zájemkyně.");
    if (typ === "zkusebni" && !vybranaZajemkyne) {
      if (jmenoVolne.trim().split(/\s+/).length < 2) return setChyba("Zadej celé jméno zájemkyně (jméno a příjmení).");
      if (telefon.replace(/\D/g, "").slice(-9).length !== 9) return setChyba("Zadej telefon zájemkyně (9 číslic), založí se jako lead.");
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim())) return setChyba("Zadej e-mail zájemkyně, založí se jako lead.");
    }
    if (!datum || !cas) return setChyba("Vyplň datum a čas.");
    setBusy(true);
    // Server umí místo tvrdé chyby požádat o potvrzení (neaktivní klientka bez
    // vstupů, dvě rezervace v jednu hodinu). Potvrzení se sbírají postupně.
    const potvrzeni = {};
    const posli = () => post({
      akce: "vytvorit", trener, typ, klient_id: T.klientka ? vybrana?.notion_id : vybranaZajemkyne?.notion_id, dite_id: T.klientka ? vybrana?.dite_id : undefined, lead_id: vybranaZajemkyne?.lead_id, klient_name: T.klientka ? undefined : (vybranaZajemkyne?.jmeno || jmenoVolne.trim()), klient_telefon: T.klientka ? undefined : (telefon.trim() || vybranaZajemkyne?.telefon || undefined), klient_email: typ === "zkusebni" ? (email.trim() || vybranaZajemkyne?.email || undefined) : undefined,
      datum, cas, delka_min: delka,
      lokalita, poznamka, opakovat: opakovat && typ === "osobni" ? { platnost_do: platnostDo || null } : null,
      ...potvrzeni,
    });
    const OTAZKY = {
      potvrdit_neaktivni: {
        klic: "potvrzeno",
        title: "Naplánovat i tak?",
        details: "O naplánování se zapíše zpráva do Slacku #automatizace.",
      },
      klientka_obsazena: {
        klic: "potvrzeno_duplicita",
        title: "Dvě rezervace v jednu hodinu?",
        details: "Každá rezervace se počítá jako samostatný odchozený trénink, klientce se odečtou dva vstupy a trenérovi se započítají dva tréninky.",
      },
    };
    try {
      let r;
      for (let pokus = 0; ; pokus++) {
        try {
          r = await posli();
          break;
        } catch (e1) {
          const otazka = OTAZKY[e1.kod];
          if (!otazka || potvrzeni[otazka.klic] || pokus > 3) throw e1;
          const potvrd = await confirmDialog({
            title: otazka.title,
            description: e1.message,
            details: otazka.details,
            confirmLabel: "Naplánovat",
            variant: "warning",
          });
          if (!potvrd) { setBusy(false); return; }
          potvrzeni[otazka.klic] = true;
        }
      }
      if (opakovat) toast(`Pravidelný termín založen, vytvořeno ${r.vytvoreno} tréninků${r.preskoceno?.length ? `, ${r.preskoceno.length} přeskočeno (kolize nebo firemní volno)` : ""}.`, "success");
      else toast(typ === "zkusebni" ? "Zkušební trénink zapsaný." : typ === "nutricni" ? "Konzultace zapsaná." : typ === "skoleni" ? "Školení zapsané." : "Rezervace vytvořena.", "success");
      onDone();
    } catch (e) { setChyba(e.message); } finally { setBusy(false); }
  };
  const hod = []; for (let h = data?.hod_od ?? 6; h < (data?.hod_do ?? 22); h++) { hod.push(hh(h)); if (T.krok === 30) hod.push(`${String(h).padStart(2, "0")}:30`); }
  const dowLabel = datum ? DNY_DLOUHE[((new Date(datum + "T00:00:00").getDay() + 6) % 7) + 1] : "";

  return (
    <ModalObal onClose={onClose} title={init.spojitS ? "Přidat klientku ke tréninku" : typ === "osobni" ? "Nová rezervace" : T.volno ? "Volno trenéra" : `Nový: ${T.nazev.toLowerCase()}`} icon={<CalendarPlus className="w-5 h-5" />}>
      {init.spojitS && <div className="text-xs bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl px-3 py-2">Spojený trénink s: <b className="demo-sensitive">{init.spojitS.klient_name}</b>, {fmtCz(init.spojitS.datum)} {init.spojitS.cas}</div>}
      {!init.spojitS && typy.length > 1 && (
        <div>
          <label className={POPISEK}>Typ</label>
          <div className="flex rounded-2xl border border-slate-200 bg-slate-100 p-1">
            {typy.map((k) => <button key={k} type="button" onClick={() => setTyp(k)} className={`flex-1 min-h-[44px] md:min-h-[36px] rounded-xl text-sm font-semibold ${typ === k ? "border border-slate-200 bg-white text-brand-deep shadow-sm" : "text-slate-600"}`}>{TYPY_REZERVACE[k].kratce}</button>)}
          </div>
        </div>
      )}
      {T.volno ? (
        <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2">Hodiny, kdy nejsi k dispozici (lékař, odpoledne pryč). Klientkám se nenabízejí. Delší nepřítomnost (týden a víc) hlas head coachovi seniorovi, nejméně 3 týdny dopředu.</div>
      ) : T.klientka ? (
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs font-semibold text-slate-600">Klientka</label>
            <label className="text-xs text-slate-500 inline-flex items-center gap-1.5 min-h-[28px]"><input type="checkbox" className="w-4 h-4 accent-brand-600" checked={jenMoje} onChange={(e) => setJenMoje(e.target.checked)} /> jen moje klientky</label>
          </div>
          <ClientAutocomplete value={jmeno} onChange={setJmeno} options={seznam.map((k) => k.label)} accentColor="violet" autoFocus={!init.spojitS && typeof window !== "undefined" && window.innerWidth >= 768} placeholder="Začni psát jméno" inputClassName="h-12 md:h-auto rounded-xl md:rounded-lg text-base md:text-sm" />
          {vybrana && <div className="text-xs text-slate-500 mt-1">{vybrana.stav || ""}{vybrana.balicek ? `, ${vybrana.balicek}` : ""}{vybrana.stav_treninku != null && vybrana.stav_treninku !== "" ? `, zůstatek ${vybrana.stav_treninku}` : ""}</div>}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <div>
            <label className={POPISEK}>{typ === "skoleni" ? "Školený trenér" : "Zájemkyně"}</label>
            {typ === "zkusebni"
              ? <ClientAutocomplete value={jmenoVolne} onChange={(v) => { setJmenoVolne(v); const z = zajemkyne.find((x) => x.label === v); if (z?.lokalita) setLokalita(z.lokalita); }} options={zajemkyne.map((z) => z.label)} accentColor="violet" placeholder="Lead, nebo jméno klientky" inputClassName="h-12 md:h-auto rounded-xl md:rounded-lg text-base md:text-sm" />
              : <input type="text" value={jmenoVolne} onChange={(e) => setJmenoVolne(e.target.value)} className="field" placeholder="Jméno nového trenéra" />}
            {typ === "zkusebni" && <div className="text-xs text-slate-500 mt-1">{vybranaZajemkyne ? (vybranaZajemkyne.lead ? `Zájemkyně z leadů${vybranaZajemkyne.telefon ? `, ${vybranaZajemkyne.telefon}` : ""}` : "Existující klientka, trénink se naváže na její kartu") : "Vyber z leadů nebo klientek. Nové jméno se založí jako lead, vyplň celé jméno, telefon a e-mail."}</div>}
          </div>
          {typ === "zkusebni" && !vybranaZajemkyne && (
            <div className="grid grid-cols-1 gap-2 sm:col-span-1">
              <div>
                <label className={POPISEK}>Telefon</label>
                <input type="tel" value={telefon} onChange={(e) => setTelefon(e.target.value)} className="field" placeholder="777 123 456" />
              </div>
              <div>
                <label className={POPISEK}>E-mail</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="field" placeholder="jmeno@email.cz" />
              </div>
            </div>
          )}
        </div>
      )}
      {T.volno ? (
        <>
          <div className="grid grid-cols-1 gap-2">
            <div>
              <label className={POPISEK}>Den {dowLabel && <span className="text-slate-400 font-normal">({dowLabel})</span>}</label>
              <DatumPole value={datum} onChange={(d) => { setDatum(d); if (volnoDoDne && volnoDoDne < d) setVolnoDoDne(""); }} />
            </div>
            <div>
              <label className={POPISEK}>Do dne <span className="text-slate-400 font-normal">(nepovinné)</span></label>
              <DatumPole value={volnoDoDne} onChange={setVolnoDoDne} min={datum} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {RYCHLE.map(([k, n, o, d]) => (
              <button key={k} type="button" onClick={() => { setVolnoOd(o); setVolnoDo(d); }} className={`min-h-[40px] rounded-full border px-2 text-sm font-bold ${volnoRychle === k ? "border-brand bg-brand-50 text-brand-700" : "border-slate-200 bg-white text-brand-700"}`}>{n}</button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={POPISEK}>Od</label>
              <div className="vyber"><Clock /><span className="min-w-0 flex-1 truncate">{hh(volnoOd)}</span><span className="vyber-sipka"><ChevronDown /></span>
                <select value={volnoOd} onChange={(e) => { const v = Number(e.target.value); setVolnoOd(v); if (volnoDo <= v) setVolnoDo(Math.min(hodDo, v + 1)); }} aria-label="Od">{hodinyCele.slice(0, -1).map((h) => <option key={h} value={h}>{hh(h)}</option>)}</select></div>
            </div>
            <div>
              <label className={POPISEK}>Do</label>
              <div className="vyber"><Clock /><span className="min-w-0 flex-1 truncate">{hh(volnoDo)}</span><span className="vyber-sipka"><ChevronDown /></span>
                <select value={volnoDo} onChange={(e) => setVolnoDo(Number(e.target.value))} aria-label="Do">{hodinyCele.filter((h) => h > volnoOd).map((h) => <option key={h} value={h}>{hh(h)}</option>)}</select></div>
            </div>
          </div>
        </>
      ) : (
      <div>
        <label className={POPISEK}>Datum {dowLabel && <span className="text-slate-400 font-normal">({dowLabel})</span>}</label>
        <DatumPole value={datum} onChange={setDatum} disabled={!!init.spojitS} />
      </div>
      )}
      {!T.volno && <div className="grid grid-cols-2 gap-2">
        <div>
          <label className={POPISEK}>Čas</label>
          <div className="vyber" aria-disabled={init.spojitS ? "true" : undefined}>
            <Clock />
            <span className="min-w-0 flex-1 truncate">{cas || "Vyber"}</span>
            <span className="vyber-sipka"><ChevronDown /></span>
            <select value={cas} onChange={(e) => setCas(e.target.value)} disabled={!!init.spojitS} aria-label="Čas">
              <option value="">Vyber</option>
              {jenBudouci(datum, hod).map((h) => <option key={h} value={h}>{h}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className={POPISEK}>Délka</label>
          <div className="vyber">
            <Timer />
            <span className="min-w-0 flex-1 truncate">{`${delka} min`}</span>
            <span className="vyber-sipka"><ChevronDown /></span>
            <select value={delka} onChange={(e) => setDelka(Number(e.target.value))} aria-label="Délka">
              {(typ === "osobni" ? [60, 120] : typ === "zkusebni" ? [90, 60] : [60, 30]).map((m) => <option key={m} value={m}>{m} min</option>)}
            </select>
          </div>
        </div>
      </div>}
      {!T.volno && <div>
        <label className={POPISEK}>Pobočka</label>
        {/* 3.9.2026: klientka je vázaná na svoje místo, tady se nevybírá. */}
        <PobockaPole nazev={nazevLokality(data, lokalita)} />
      </div>}
      {!init.spojitS && typ === "osobni" && (
        <div className="space-y-3">
          <Zapinatko zapnuto={opakovat} onZmena={setOpakovat} popisek="Opakovat každý týden" ikona={Repeat} />
          {opakovat && <div className="text-xs text-slate-500 mt-1">{[dowLabel, cas].filter(Boolean).join(" ") || "Každý týden"}, do konce balíčku</div>}
          {opakovat && (
            <div>
              <label className={POPISEK}>Platí do <span className="text-slate-400 font-normal">(nepovinné)</span></label>
              <DatumPole value={platnostDo} onChange={setPlatnostDo} min={datum} />
              <div className="text-xs text-slate-400 mt-1">Prázdné = podle balíčku: GOLD 24 týdnů, PREMIUM 48 týdnů, FLEXIBLE 2 měsíce dopředu.</div>
            </div>
          )}
        </div>
      )}
      <div>
        <label className={POPISEK}>{T.volno ? "Důvod" : "Poznámka"} <span className="text-slate-400 font-normal">(nepovinné)</span></label>
        <input type="text" value={poznamka} onChange={(e) => setPoznamka(e.target.value)} className="field" placeholder={T.volno ? "např. lékař" : "např. první trénink po dovolené"} maxLength={T.volno ? 80 : undefined} />
      </div>
      {chyba && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2">{chyba}</div>}
      <PatkaOkna>
        <button onClick={onClose} className="btn btn-secondary">Zavřít</button>
        <button onClick={odeslat} disabled={busy} className="btn btn-primary"><Check className="w-5 h-5 md:w-4 md:h-4" />{busy ? "Ukládám" : typ === "osobni" ? "Rezervovat" : T.volno ? (volnoDny.length > 1 ? `Uložit volno na ${volnoDny.length} dny` : `Uložit volno ${hh(volnoOd)} až ${hh(volnoDo)}`) : "Zapsat"}</button>
      </PatkaOkna>
    </ModalObal>
  );
}

// ─── modal: detail rezervace ─────────────────────────────────────────────────
// Seznam akcí v detailu (1. 10. 2026, mockup B): řádek = ikona v dlaždici, název,
// vpravo pilulka s tím, co se stane. `ton`: "bad" červeně (zrušit, zamítnout),
// "warn" oranžově (propadnutí), jinak fialově. Prázdné položky (false) se vynechají.
export function SeznamAkci({ polozky }) {
  const radky = (polozky || []).filter(Boolean);
  if (!radky.length) return null;
  const TON = {
    bad: { ikona: "bg-rose-50 text-rose-700", text: "text-rose-700", pilulka: "bg-rose-50 text-rose-700" },
    warn: { ikona: "bg-amber-50 text-amber-800", text: "text-slate-900", pilulka: "bg-amber-50 text-amber-800" },
    brand: { ikona: "bg-brand-50 text-brand-700", text: "text-slate-900", pilulka: "bg-brand-50 text-brand-700" },
  };
  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
      {radky.map(({ klic, Ikona, nazev, popis, ton, onClick, disabled }) => { const t = TON[ton] || TON.brand; return (
        <button key={klic} type="button" onClick={onClick} disabled={disabled}
          className="flex w-full items-center gap-3 border-t border-slate-100 px-3 py-2.5 text-left transition first:border-t-0 hover:bg-slate-50 active:bg-brand-50 disabled:opacity-50 min-h-[56px]">
          <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-xl ${t.ikona}`}>{Ikona && <Ikona className="h-[18px] w-[18px]" />}</span>
          <span className={`min-w-0 flex-1 text-[15px] font-bold ${t.text}`}>{nazev}</span>
          {popis && <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${t.pilulka}`}>{popis}</span>}
        </button>
      ); })}
    </div>
  );
}

export function DetailModal({ rez, skupina, data, trener, user, onClose, onDone, setConfirm, onPridat, onKlientka }) {
  const [rezimAkce, setRezimAkce] = useState(null); // 'presun' | 'zruseni' | 'zamitnuti'
  // Rezervace z aplikace čekající na potvrzení (29. 9. 2026): Potvrdit, Zamítnout, nebo Přesunout
  // (přesun = potvrzení s novým termínem, klientku informuje trenér osobně).
  const ceka = rez.status === "ceka";
  const [datum, setDatum] = useState(rez.datum);
  const [cas, setCas] = useState(rez.cas);
  const [lokalita, setLokalita] = useState(rez.lokalita || data?.trener?.lokalita || lokalityZ(data)[0].kod);
  // FW-SERIE (19. 10. 2026): pravidelný termín, ke kterému trénink patří (i bez propojení),
  // a volba trenéra, co s ním. Bez volby přesun ani zrušení neprojde.
  const [serieRez, setSerieRez] = useState(null);
  const [serieVolba, setSerieVolba] = useState(null); // 'jen' | 'prepsat' | 'ukoncit'
  useEffect(() => {
    // U spojeného tréninku se hledá u každé klientky, k sérii může patřit kterákoli.
    const ids = (skupina?.polozky || [rez]).filter((p) => p.zdroj === "fw40" && p.booking_id).map((p) => p.booking_id);
    if (!ids.length) return;
    let zive = true;
    Promise.all(ids.map((id) => apiFetch(`/api/rezervace?akce=serie_k_rezervaci&booking_id=${encodeURIComponent(id)}`).then((r) => (r.ok ? r.json() : null)).catch(() => null)))
      .then((vse) => { if (zive) setSerieRez(vse.map((j) => j?.serie).find(Boolean) || null); });
    return () => { zive = false; };
  }, [rez.booking_id, rez.zdroj, skupina]); // eslint-disable-line react-hooks/exhaustive-deps
  const [typZruseni, setTypZruseni] = useState("vcas");
  const [duvod, setDuvod] = useState("");
  const [busy, setBusy] = useState(false);
  const [chyba, setChyba] = useState("");
  const dnes = isoOf(pragueDate());
  // 29. 9. 2026: zkušební a nutriční jsou plnohodnotné rezervace; jen řádky z Reservia
  // jsou do přepnutí ke čtení (jako osobní tréninky). Školení se nepočítá do docházky.
  const reservioZamek = rez.zdroj !== "fw40" && data?.rezim !== "vlastni";
  const zkusebni = jeZkusebni(rez.sluzba) && reservioZamek;
  const typR = typRezervace(rez);
  // 8. 10. 2026: u nutriční konzultace texty zrušení mluví o konzultaci, ne o tréninku.
  const jeKonz = typR === "nutricni";
  const minulost = rez.datum < dnes;
  const zaMin = (() => { const [y, m, d] = rez.datum.split("-").map(Number); const [yy, mm, dd] = dnes.split("-").map(Number); const dnu = Math.round((Date.UTC(y, m - 1, d) - Date.UTC(yy, mm - 1, dd)) / 86400000); const n = pragueDate(); return dnu * 1440 + (casMin(rez.cas) || 0) - (n.getHours() * 60 + n.getMinutes()); })();
  const pozde = zaMin < 24 * 60;
  useEffect(() => { if (rezimAkce === "zruseni") setTypZruseni(pozde ? "pozde" : "vcas"); }, [rezimAkce, pozde]);
  const hod = []; for (let h = data?.hod_od ?? 6; h < (data?.hod_do ?? 22); h++) hod.push(h);
  // Zkušební z našeho kalendáře (1. 10. 2026, varianta A): karta nové klientky z kontaktu u sales.
  // 2. 10. 2026: vráceno, omylem zmizelo v 90cdc4e7.
  const [karta, setKarta] = useState(null);
  // Deal u trenéra (18. 10. 2026): po zkušebním se z okna otevře průvodce, okno rezervace se mezitím schová.
  const [dealVolba, setDealVolba] = useState(null);
  useEffect(() => {
    if (typR !== "zkusebni" || !rez.booking_id) return;
    let zive = true;
    apiFetch(`/api/rezervace?akce=zkusebni_karta&booking_id=${encodeURIComponent(rez.booking_id)}`)
      .then((r) => (r.ok ? r.json() : null)).then((j) => { if (zive && j?.karta) setKarta(j.karta); }).catch(() => {});
    return () => { zive = false; };
  }, [rez.booking_id, rez.zdroj, typR]);

  // Spojený trénink (2. 10. 2026, zpětná vazba trenérů): u přesunu a zrušení se nejdřív
  // vybere, koho se to týká, celý trénink (výchozí) nebo jen jedna klientka.
  const skupinaPolozky = (skupina?.polozky || [rez]).filter((p) => p.propadnuti !== "ano" && p.status !== "zruseno");
  const spojeny = skupinaPolozky.length > 1;
  const [koho, setKoho] = useState("vse"); // 'vse' | booking_id
  const cile = spojeny ? (koho === "vse" ? skupinaPolozky : skupinaPolozky.filter((p) => p.booking_id === koho)) : [rez];
  const prijmeni = (p) => String(p.klient_name || "").trim().split(/\s+/).pop();
  const kohoPopis = !spojeny ? "" : koho === "vse" ? "obě klientky" : (cile[0]?.klient_name || "");
  const KohoVyber = ({ akce }) => spojeny ? (
    <div className="space-y-1.5">
      <div className="text-xs font-semibold text-slate-500">{akce === "presun" ? "Koho přesunout" : "Komu zrušit"}</div>
      {[{ id: "vse", nazev: "Celý trénink", pozn: "obě klientky" }, ...skupinaPolozky.map((p) => ({ id: p.booking_id, nazev: `Jen ${p.klient_name}`, pozn: `${skupinaPolozky.filter((q) => q.booking_id !== p.booking_id).map(prijmeni).join(", ")} ${akce === "presun" ? "zůstává" : "chodí dál"}` }))].map((o) => (
        <label key={o.id} className={`flex min-h-[48px] cursor-pointer items-center gap-3 rounded-xl border px-3 py-2 text-sm font-semibold ${koho === o.id ? "border-brand bg-brand-50" : "border-slate-200"}`}>
          <input type="radio" className="h-4 w-4 accent-brand-600" checked={koho === o.id} onChange={() => setKoho(o.id)} />
          <span className="min-w-0 flex-1">{o.nazev}</span>
          <span className="shrink-0 text-xs font-medium text-slate-500">{o.pozn}</span>
        </label>
      ))}
    </div>
  ) : null;
  // 6. 10. 2026: kdo rezervoval a kdo potvrdil (z historie rezervace), jen u našich rezervací.
  const [historie, setHistorie] = useState(null);
  useEffect(() => {
    if (rez.zdroj !== "fw40" || !rez.booking_id) return;
    let zive = true;
    apiFetch(`/api/rezervace?akce=historie&booking_id=${encodeURIComponent(rez.booking_id)}`)
      .then((r) => (r.ok ? r.json() : null)).then((j) => { if (zive && j?.historie) setHistorie(j.historie); }).catch(() => {});
    return () => { zive = false; };
  }, [rez.booking_id, rez.zdroj, rez.status]);
  const zAplikace = /^portal:/.test(rez.created_by || "");
  const kdoText = (h) => (h.odkud === "portal" ? "klientka v aplikaci" : h.odkud === "cron" ? "systém" : String(h.kdo || "tým").replace(/@.*$/, ""));
  const kdyText = (h) => { const [d, t] = String(h.kdy || "").split(" "); return d ? `${fmtCz(d)} ${t || ""}`.trim() : ""; };
  const zalozeni = historie?.find((h) => h.akce === "vytvoreno_ceka" || h.akce === "vytvoreno");
  const potvrzeni = historie?.find((h) => h.akce === "potvrzeno");
  const presuny = historie?.filter((h) => h.akce === "presunuto") || [];
  // Manažer, owner nebo CSM potvrzuje za trenéra: jiný popisek a trenérovi jde zpráva.
  const jaTrener = String(user?.role || "").toLowerCase() === "trener" || (user?.linkedTrener && user.linkedTrener === (rez.trener_name || trener));
  const krestniTrenera = String(rez.trener_name || trener || "").split(" ")[0];
  const presun = async () => {
    setChyba(""); setBusy(true);
    try {
      let varovani = null;
      for (const p of cile) { const r = await post({ akce: "presunout", booking_id: p.booking_id, datum, cas, lokalita, serie_volba: serieVolba || undefined }); if (r?.serie_varovani) varovani = r.serie_varovani; }
      toast(ceka ? "Termín přesunut a potvrzen." : cile.length > 1 ? "Trénink přesunut, obě klientky dostanou e-mail." : serieVolba === "prepsat" && !varovani ? "Trénink i pravidelný termín přesunuty." : serieVolba === "ukoncit" ? "Trénink přesunut, pravidelný termín ukončen." : "Trénink přesunut.", "success");
      if (varovani) toast(`Pravidelný termín na nový čas nevznikl. ${varovani}`, "error");
      onDone();
    }
    catch (e) { if (e.kod === "serie_volba" && e.detail?.serie) setSerieRez(e.detail.serie); setChyba(e.message); } finally { setBusy(false); }
  };
  const potvrdit = async () => {
    setChyba(""); setBusy(true);
    try { await post({ akce: "potvrdit", booking_id: rez.booking_id }); toast("Rezervace potvrzena, klientce jde e-mail.", "success"); onDone(); }
    catch (e) { setChyba(e.message); } finally { setBusy(false); }
  };
  const zamitnout = async () => {
    setChyba(""); setBusy(true);
    try { await post({ akce: "zamitnout", booking_id: rez.booking_id, duvod }); toast("Rezervace zamítnuta, klientce jde e-mail.", "success"); onDone(); }
    catch (e) { setChyba(e.message); } finally { setBusy(false); }
  };
  const zrusit = async () => {
    setChyba(""); setBusy(true);
    try {
      let r = null;
      for (const p of cile) r = await post({ akce: "zrusit", booking_id: p.booking_id, propadnuti: typZruseni === "pozde", duvod, serie_volba: typZruseni === "vcas" ? (serieVolba || undefined) : undefined });
      toast(r?.propadnuti ? (jeKonz ? "Konzultace označena jako propadlá." : "Trénink označen jako propadlý.") : cile.length > 1 ? "Trénink zrušen oběma klientkám." : jeKonz ? "Konzultace zrušena." : "Trénink zrušen.", "success"); onDone();
    } catch (e) { if (e.kod === "serie_volba" && e.detail?.serie) setSerieRez(e.detail.serie); setChyba(e.message); } finally { setBusy(false); }
  };
  const propadnutiToggle = async () => {
    setChyba(""); setBusy(true);
    try { await post({ akce: "propadnuti", booking_id: rez.booking_id, hodnota: rez.propadnuti !== "ano" }); toast(rez.propadnuti === "ano" ? "Propadnutí vráceno." : "Trénink označen jako propadlý.", "success"); onDone(); }
    catch (e) { setChyba(e.message); } finally { setBusy(false); }
  };

  if (dealVolba) return <DialogZkusebni volba={dealVolba} onZpet={() => setDealVolba(null)} onHotovo={onDone} />;

  return (
    <ModalObal onClose={onClose} title={typR !== "osobni" ? TYPY_REZERVACE[typR].nazev : skupina?.polozky?.length > 1 ? "Spojený trénink" : "Trénink"} icon={<Calendar className="w-5 h-5" />}>
      {ceka && <div className="flex items-center gap-2 text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded-xl px-3 py-2"><Clock className="w-4 h-4 shrink-0" /><span><b>Čeká na potvrzení.</b> Klientka rezervovala v aplikaci. Potvrď, zamítni, nebo přesuň na jiný termín.</span></div>}
      <div className="space-y-1.5 text-sm">
        {(skupina?.polozky || [rez]).map((p, i) => (
          <Radek key={p.booking_id} k={i === 0 ? "Klientka" : "Spolu s"} v={
            <span className="demo-sensitive inline-flex items-center gap-2 flex-wrap justify-end">
              {onKlientka && p.klient_id
                ? <button type="button" onClick={() => onKlientka(p)} className="inline-flex items-center gap-1 text-brand-700 hover:underline" title="Karta klientky (otevře se nad kalendářem)"><Users className="w-4 h-4" />{p.klient_name}</button>
                : p.klient_id ? <Link to={profilKlientky(user, p.klient_name)} className="text-brand-700 hover:underline" title="Otevřít profil klientky">{p.klient_name}</Link> : <span className="font-semibold text-slate-800">{p.klient_name}</span>}
              {p.klient_telefon ? <a className="text-xs text-slate-500 hover:text-brand-700" href={`tel:+420${p.klient_telefon}`}>{p.klient_telefon}</a> : null}
              {p.propadnuti === "ano" ? <span className="text-[11px] uppercase text-rose-700 font-bold">propadl</span> : null}
            </span>
          } />
        ))}
        <Radek k="Kdy" v={`${DNY_DLOUHE[((new Date(rez.datum + "T00:00:00").getDay() + 6) % 7) + 1]} ${fmtCzRok(rez.datum)}, ${rez.cas}${rez.cas_konec ? ` až ${rez.cas_konec}` : ""}`} />
        <Radek k="Kde" v={typR === "nutricni" ? "online" : nazevLokality(data, rez.lokalita)} />
        <Radek k="Trenér" v={rez.trener_name || trener} />
        {zAplikace
          ? <Radek k="Rezervovala" v={<span className="inline-flex items-center gap-1.5"><Smartphone className="h-3.5 w-3.5 text-emerald-700" />v aplikaci{zalozeni ? `, ${kdyText(zalozeni)}` : ""}</span>} />
          : <Radek k="Zdroj" v={rez.zdroj === "fw40" ? `náš kalendář${zalozeni ? `, ${kdoText(zalozeni)} ${kdyText(zalozeni)}` : rez.created_by ? ` (${String(rez.created_by).replace(/@.*$/, "")})` : ""}` : "Reservio"} />}
        {ceka && <Radek k="Potvrzení" v={<span className="rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-bold text-amber-800">čeká na potvrzení</span>} />}
        {!ceka && potvrzeni && <Radek k="Potvrdil" v={<span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-bold text-emerald-700">{kdoText(potvrzeni)} <span className="mx-1 opacity-40">|</span> {kdyText(potvrzeni)}</span>} />}
        {presuny.length > 0 && <Radek k={presuny.length === 1 ? "Přesunuto" : "Přesuny"} v={presuny.map((h, i) => <span key={i} className="block">{h.pred?.datum ? `z ${fmtCz(h.pred.datum)} ${h.pred.cas || ""}` : ""}{h.po?.datum ? ` na ${fmtCz(h.po.datum)} ${h.po.cas || ""}` : ""}, {kdoText(h)} {kdyText(h)}</span>)} />}
        {rez.serie_id && <Radek k="Pravidelný" v="ano, součást série" />}
        {rez.poznamka && !karta && <Radek k="Poznámka" v={rez.poznamka} />}
        {rez.propadnuti === "ano" && <div className="text-xs bg-rose-50 border border-rose-200 text-rose-800 rounded-xl px-3 py-2">{jeKonz ? "Konzultace je označená jako propadlá (pozdní zrušení). Počítá se jako proběhlá, další nárok bude mít klientka až za 90 dní." : "Trénink je označený jako propadlý (pozdní zrušení). Počítá se do výplaty i zůstatku, ne do docházky."}</div>}
      </div>

      {rez.stitek === "podepsat_smlouvu" && rez.deal_id && <BlokPodepsat dealId={rez.deal_id} onHotovo={() => { toast("Smlouva je potvrzená jako podepsaná.", "success"); onDone(); }} />}

      {typR === "zkusebni" && rez.booking_id && rez.status !== "zruseno" && <RozhodnutiZkusebni bookingId={rez.booking_id} onOtevri={setDealVolba} />}

      {karta && <KartaZkusebniTrener karta={karta} trenerId={rez.trener_id} />}

      {reservioZamek && !zkusebni && <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-3 py-2">Tenhle trénink pochází z Reservia. Do přepnutí ho měň v Reserviu, tady jde jen označit propadnutí.</div>}
      {zkusebni && <div className="text-xs bg-sky-50 border border-sky-200 text-sky-800 rounded-xl px-3 py-2">Tenhle termín pochází z Reservia, do přepnutí se tu jen zobrazuje.</div>}

      {/* 1. 10. 2026 (Láďa, mockup B): akce jako seznam řádků s ikonou a pilulkou, co se stane.
          Hlavní akce (Potvrdit) velká nahoře. Stejné v detailu tréninku i konzultace. */}
      {!rezimAkce && ceka && (
        <div className="space-y-2">
          {!jaTrener && !minulost && <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">Potvrzuješ za trenéra. {krestniTrenera || "Trenérovi"} odejde zpráva do Slacku, klientce e-mail s potvrzením.</div>}
          {!minulost && <button onClick={potvrdit} disabled={busy} className="btn w-full min-h-[52px] bg-emerald-600 text-base text-white hover:bg-emerald-700"><CheckCircle2 className="w-5 h-5" /> {jaTrener || !krestniTrenera ? "Potvrdit" : `Potvrdit za ${krestniTrenera}`}</button>}
          <SeznamAkci polozky={[
            !minulost && { klic: "presun", Ikona: ArrowRight, nazev: "Přesunout", popis: "jiný termín", onClick: () => setRezimAkce("presun") },
            { klic: "zamitnuti", Ikona: XCircle, nazev: "Zamítnout", popis: "klientce jde e-mail", ton: "bad", onClick: () => setRezimAkce("zamitnuti") },
          ]} />
        </div>
      )}
      {!rezimAkce && !ceka && (
        <SeznamAkci polozky={[
          !zkusebni && !reservioZamek && !minulost && { klic: "presun", Ikona: ArrowRight, nazev: "Přesunout", popis: "jiný den nebo hodina", onClick: () => setRezimAkce("presun") },
          !zkusebni && typR === "osobni" && !minulost && (((skupina?.polozky || [rez]).filter((p) => p.propadnuti !== "ano").length < 2) && (skupina?.polozky?.length || 1) < 3) && { klic: "pridat", Ikona: Users, nazev: "Přidat klientku", popis: "spojený trénink", onClick: () => onPridat(rez) },
          !zkusebni && typR === "osobni" && { klic: "propadnuti", Ikona: TimerOff, nazev: rez.propadnuti === "ano" ? "Vrátit propadnutí" : "Propadnutí", popis: rez.propadnuti === "ano" ? "zase se počítá běžně" : "pozdní zrušení", ton: "warn", onClick: () => setConfirm({ title: rez.propadnuti === "ano" ? "Vrátit propadnutí?" : "Označit jako propadlý?", description: rez.propadnuti === "ano" ? "Trénink přestane být propadlý a bude se počítat jako běžně absolvovaný nebo naplánovaný." : "Klientka zrušila méně než 24 hodin předem. Trénink se strhne ze zůstatku, jako by proběhl.", confirmLabel: rez.propadnuti === "ano" ? "Vrátit" : "Ano, propadl", variant: rez.propadnuti === "ano" ? "primary" : "danger", onConfirm: propadnutiToggle }) },
          !zkusebni && !reservioZamek && !minulost && rez.propadnuti !== "ano" && { klic: "zruseni", Ikona: XCircle, nazev: "Zrušit", popis: "klientce jde e-mail", ton: "bad", onClick: () => setRezimAkce("zruseni") },
        ]} />
      )}

      {rezimAkce === "presun" && (
        <div className="rounded-xl border border-slate-200 p-3 space-y-3">
          <div className="text-sm font-bold text-slate-800">Přesunout na</div>
          <KohoVyber akce="presun" />
          <DatumPole value={datum} onChange={setDatum} />
          <div className="grid grid-cols-2 gap-2">
            <div className="vyber">
              <Clock />
              <span className="min-w-0 flex-1 truncate">{cas}</span>
              <span className="vyber-sipka"><ChevronDown /></span>
              <select value={cas} onChange={(e) => setCas(e.target.value)} aria-label="Čas">{jenBudouci(datum, hod).map((h) => <option key={h} value={hh(h)}>{hh(h)}</option>)}</select>
            </div>
            <PobockaPole nazev={nazevLokality(data, lokalita)} vMrizce />
          </div>
          {serieRez && <VolbaSerie serie={serieRez} akce="presun" novyDatum={datum} novyCas={cas} volba={serieVolba} setVolba={setSerieVolba} />}
          <div className="text-xs text-slate-500">{ceka ? "Přesunem rezervaci zároveň potvrdíš. Klientku o novém termínu informuješ osobně, e-mail o změně se neposílá." : spojeny ? (koho === "vse" ? "Obě klientky dostanou e-mail o novém termínu." : `${cile[0]?.klient_name || "Klientka"} dostane e-mail o novém termínu, druhá klientka zůstává v původním čase.`) : "Klientce přijde e-mail o změně termínu."}</div>
          {chyba && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2">{chyba}</div>}
          <div className="flex justify-end gap-2"><button onClick={() => setRezimAkce(null)} className="btn btn-secondary">Zpět</button><button onClick={ceka ? () => setConfirm({ title: "Přesunout a potvrdit?", description: `Rezervace ${rez.klient_name} se přesune na ${fmtCz(datum)} v ${cas} a tím se potvrdí. Klientku o novém termínu informuješ ty osobně (SMS nebo telefon), e-mail o změně se neposílá.`, confirmLabel: "Ano, informuji klientku sám", variant: "primary", onConfirm: presun }) : presun} disabled={busy || (serieRez && !serieVolba)} className="btn btn-primary">{busy ? "Ukládám" : ceka ? "Přesunout a potvrdit" : spojeny ? (koho === "vse" ? "Přesunout obě" : "Přesunout jen vybranou") : "Přesunout"}</button></div>
        </div>
      )}

      {rezimAkce === "zamitnuti" && (
        <div className="rounded-xl border border-rose-200 p-3 space-y-3">
          <div className="text-sm font-bold text-slate-800">Zamítnout rezervaci</div>
          <div className="text-sm text-slate-600">Klientce přijde e-mail „Rezervace nebyla potvrzena“ s důvodem a v aplikaci si vybere jiný termín. Termín se uvolní.</div>
          <input type="text" value={duvod} onChange={(e) => setDuvod(e.target.value)} placeholder="Důvod pro klientku (nepovinné)" className="field" />
          {chyba && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2">{chyba}</div>}
          <div className="flex justify-end gap-2"><button onClick={() => setRezimAkce(null)} className="btn btn-secondary">Zpět</button><button onClick={zamitnout} disabled={busy} className="btn btn-danger">{busy ? "Ukládám" : "Zamítnout"}</button></div>
        </div>
      )}

      {rezimAkce === "zruseni" && (
        <div className="rounded-xl border border-rose-200 p-3 space-y-3">
          <div className="text-sm font-bold text-slate-800">{jeKonz ? "Zrušit konzultaci" : "Zrušit trénink"}</div>
          <KohoVyber akce="zruseni" />
          <div className="space-y-1.5 text-sm">
            <label className="flex items-start gap-2"><input type="radio" className="mt-1 w-4 h-4 accent-brand-600 flex-shrink-0" checked={typZruseni === "vcas"} onChange={() => setTypZruseni("vcas")} />{jeKonz
              ? <span><b>Zrušeno včas</b> (nebo ruší nutriční poradkyně). Konzultace zmizí z kalendáře a klientce zůstává nárok na další.</span>
              : <span><b>Zrušeno včas</b> (nebo ruší trenér). Trénink zmizí z kalendáře, zůstatek se nemění.</span>}</label>
            <label className="flex items-start gap-2"><input type="radio" className="mt-1 w-4 h-4 accent-brand-600 flex-shrink-0" checked={typZruseni === "pozde"} onChange={() => setTypZruseni("pozde")} />{jeKonz
              ? <span><b>Zrušeno pozdě, konzultace propadá.</b> Zůstane v kalendáři jako propadlá a počítá se jako proběhlá, další nárok bude mít klientka až za 90 dní.{pozde && <span className="text-rose-700"> Do začátku zbývá méně než 24 hodin.</span>}</span>
              : <span><b>Zrušeno pozdě, trénink propadá.</b> Zůstane v kalendáři jako propadlý, strhne se ze zůstatku a počítá se do výplaty.{pozde && <span className="text-rose-700"> Do začátku zbývá méně než 24 hodin.</span>}</span>}</label>
          </div>
          {serieRez && typZruseni === "vcas" && <VolbaSerie serie={serieRez} akce="zruseni" volba={serieVolba} setVolba={setSerieVolba} />}
          <input type="text" value={duvod} onChange={(e) => setDuvod(e.target.value)} placeholder="Důvod (nepovinné)" className="field" />
          <div className="text-xs text-slate-500">{typZruseni === "pozde" ? "Při propadnutí klientce e-mail nechodí." : spojeny ? (koho === "vse" ? "Obě klientky dostanou e-mail o zrušení." : `${cile[0]?.klient_name || "Klientka"} dostane e-mail o zrušení, druhé klientce trénink zůstává.`) : "Klientce přijde e-mail o zrušení."}</div>
          {chyba && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2">{chyba}</div>}
          <div className="flex justify-end gap-2"><button onClick={() => setRezimAkce(null)} className="btn btn-secondary">Zpět</button><button
              onClick={() => setConfirm({
                title: "Opravdu zrušit?",
                description: jeKonz
                  ? (typZruseni === "pozde"
                    ? `Konzultace ${cile.map((p) => p.klient_name).join(" a ")} ${fmtCz(rez.datum)} v ${rez.cas} se zruší jako propadlá. Počítá se jako proběhlá a další nárok bude mít klientka až za 90 dní. Propadlá konzultace zůstane v kalendáři jako propadlá.`
                    : `Konzultace ${cile.map((p) => p.klient_name).join(" a ")} ${fmtCz(rez.datum)} v ${rez.cas} se zruší jako zrušená včas. Nepropadá, klientce zůstává nárok na další konzultaci a přijde jí e-mail. Zrušená konzultace zmizí z kalendáře, takže ji pak už nepůjde označit jako propadlou.`)
                  : typZruseni === "pozde"
                  ? `Trénink ${cile.map((p) => p.klient_name).join(" a ")} ${fmtCz(rez.datum)} v ${rez.cas} se zruší jako propadlý. Strhne se ze zůstatku klientky a počítá se ti do výplaty. Propadlý trénink zůstane v kalendáři přeškrtnutý.`
                  : `Trénink ${cile.map((p) => p.klient_name).join(" a ")} ${fmtCz(rez.datum)} v ${rez.cas} se zruší jako zrušený včas. Nepropadá, klientce trénink zůstává na kontě, a přijde jí e-mail. Zrušený trénink zmizí z kalendáře, takže ho pak už nepůjde označit jako propadlý.`,
                confirmLabel: typZruseni === "pozde" ? "Ano, zrušit a propadá" : "Ano, zrušit",
                variant: "danger",
                onConfirm: zrusit,
              })}
              disabled={busy || (serieRez && typZruseni === "vcas" && !serieVolba)}
              className="btn btn-danger"
            >{busy ? "Ukládám" : typZruseni === "pozde" ? (jeKonz ? "Zrušit, konzultace propadá" : "Zrušit, trénink propadá") : jeKonz ? "Zrušit konzultaci" : spojeny ? (koho === "vse" ? "Zrušit oběma" : "Zrušit jen vybrané") : "Zrušit trénink"}</button></div>
        </div>
      )}
      {chyba && !rezimAkce && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2">{chyba}</div>}
      <PatkaOkna><button onClick={onClose} className="btn btn-secondary w-full sm:w-auto">Zavřít</button></PatkaOkna>
    </ModalObal>
  );
}

// FW-SERIE (19. 10. 2026): volba u tréninku, který patří k pravidelnému termínu.
// Žádná volba není předvybraná, trenér musí rozhodnout (schválený návrh 19. 10.).
const KAZDY_DEN = ["", "každé pondělí", "každé úterý", "každou středu", "každý čtvrtek", "každý pátek", "každou sobotu", "každou neděli"];
function VolbaSerie({ serie, akce, novyDatum, novyCas, volba, setVolba }) {
  const kazdy = KAZDY_DEN[serie.dow] || "";
  const novyKazdy = novyDatum ? KAZDY_DEN[((new Date(novyDatum + "T00:00:00").getDay() + 6) % 7) + 1] : "";
  const zruseni = serie.budoucich > 0 ? `Další tréninky z něj (${serie.budoucich}) se zruší, nové už nevzniknou.` : "Nové tréninky z něj už nevzniknou.";
  const moznosti = akce === "presun" ? [
    { k: "jen", nazev: "Přesunout jen tento trénink", pozn: `Pravidelný termín zůstává ${kazdy} ${serie.cas}.` },
    { k: "prepsat", nazev: "Změnit i pravidelný termín", pozn: `Od dalšího týdne ${novyKazdy} ${novyCas}.${serie.budoucich > 0 ? ` Další tréninky z původního termínu (${serie.budoucich}) nahradí nové.` : ""}` },
    { k: "ukoncit", nazev: "Přesunout a pravidelný termín ukončit", pozn: zruseni },
  ] : [
    { k: "jen", nazev: "Zrušit jen tento trénink", pozn: `Pravidelný termín zůstává, další týden normálně proběhne.` },
    { k: "ukoncit", nazev: "Zrušit a pravidelný termín ukončit", pozn: zruseni },
  ];
  return (
    <div className="space-y-2">
      <div className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-900"><b>Tento trénink patří k pravidelnému termínu</b> {kazdy} v {serie.cas}. Co s ním?</div>
      {moznosti.map((m) => (
        <label key={m.k} className={`flex cursor-pointer items-start gap-3 rounded-xl border px-3 py-2.5 text-sm ${volba === m.k ? "border-brand bg-brand-50 ring-2 ring-brand/15" : "border-slate-200 bg-white"}`}>
          <input type="radio" className="mt-0.5 h-4 w-4 flex-shrink-0 accent-brand-600" checked={volba === m.k} onChange={() => setVolba(m.k)} />
          <span className="min-w-0"><b className="text-slate-900">{m.nazev}</b><span className="block text-slate-600">{m.pozn}</span></span>
        </label>
      ))}
      {volba && volba !== "jen" && (
        <div className="text-xs text-slate-500">
          {serie.rucnich > 0 && <>Ručně založené tréninky ve stejném čase ({serie.rucnich}) se nemění, uprav je zvlášť. </>}
          Klientce o změně pravidelného termínu dej vědět, e-mail jí přijde jen o tomto tréninku.
        </div>
      )}
      {!volba && <div className="text-xs font-semibold text-rose-700">Vyber, co s pravidelným termínem.</div>}
    </div>
  );
}

// FW-SERIE (19. 10. 2026): pravidelné termíny, které po prodloužení spolupráce čekají
// na trenéra. Dokud nerozhodne, nic dalšího se nenaplánuje.
export function SerieCekaji({ data, onChange }) {
  const cekaji = (data?.serie || []).filter((s) => s.ceka_od);
  const [busy, setBusy] = useState("");
  if (!cekaji.length) return null;
  // Série pokračuje (a případně končí) až za posledním naplánovaným tréninkem, ty zůstávají.
  const poNaplanovanych = (s) => { const dnes = isoOf(pragueDate()); const d = new Date(((s.generovano_do && s.generovano_do >= dnes) ? s.generovano_do : dnes) + "T00:00:00"); d.setDate(d.getDate() + 1); return isoOf(d); };
  const prvniOd = (s) => { const d = new Date(poNaplanovanych(s) + "T00:00:00"); let i = 0; while (i < 8 && ((d.getDay() + 6) % 7) + 1 !== Number(s.dow)) { d.setDate(d.getDate() + 1); i++; } return fmtCz(isoOf(d)); };
  const akce = async (s, typ) => {
    if (typ === "ukoncit") {
      const ok = await confirmDialog({ title: "Ukončit pravidelný termín?", description: `${s.klient_name}, ${KAZDY_DEN[s.dow]} ${s.cas}. Už naplánované tréninky zůstanou, nové po nich nevzniknou.`, confirmLabel: "Ukončit", variant: "danger" });
      if (!ok) return;
    }
    setBusy(s.id + typ);
    try {
      if (typ === "pokracovat") { const r = await post({ akce: "serie_pokracovat", serie_id: s.id }); toast(`Pravidelný termín pokračuje, naplánováno ${r.vytvoreno} tréninků${r.preskoceno?.length ? `, ${r.preskoceno.length} přeskočeno (kolize nebo firemní volno)` : ""}.`, "success"); }
      else { await post({ akce: "serie_ukoncit", serie_id: s.id, od: poNaplanovanych(s) }); toast("Pravidelný termín ukončen, naplánované tréninky zůstaly.", "success"); }
      onChange?.();
    } catch (e) { toast(e.message, "error"); } finally { setBusy(""); }
  };
  return (
    <div className="space-y-2">
      {cekaji.map((s) => (
        <div key={s.id} className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-white p-3 sm:p-4 shadow-sm">
          <div className="hidden sm:flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700"><Repeat className="h-5 w-5" /></div>
          <div className="min-w-0 flex-1">
            <div className="font-black text-slate-900">Pravidelný termín čeká na rozhodnutí</div>
            <div className="mt-0.5 text-sm text-slate-600"><span className="demo-sensitive">{s.klient_name}</span> prodloužila spolupráci. Plánovat dál pravidelný trénink?</div>
            <div className="mt-2 flex flex-wrap gap-1.5 text-xs font-semibold">
              <span className="rounded-full border border-brand-200 bg-brand-50 px-2.5 py-1 text-brand-deep">{KAZDY_DEN[s.dow]} {s.cas}</span>
              {s.balicek && <span className="rounded-full border border-brand-200 bg-brand-50 px-2.5 py-1 text-brand-deep">{s.balicek}</span>}
              {s.ceka_konec && <span className="rounded-full border border-brand-200 bg-brand-50 px-2.5 py-1 text-brand-deep">nově do {fmtCzRok(s.ceka_konec)}</span>}
            </div>
            <div className="mt-3 grid gap-2 sm:flex">
              <button type="button" className="btn btn-primary" disabled={!!busy} onClick={() => akce(s, "pokracovat")}>{busy === s.id + "pokracovat" ? "Plánuji" : `Pokračovat od ${prvniOd(s)}`}</button>
              <button type="button" className="btn btn-secondary" disabled={!!busy} onClick={() => akce(s, "ukoncit")}>{busy === s.id + "ukoncit" ? "Ukončuji" : "Ukončit pravidelný termín"}</button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function Radek({ k, v }) {
  return <div className="flex justify-between items-baseline gap-3"><span className="text-slate-500 text-xs">{k}</span><span className="font-semibold text-slate-800 text-right">{v}</span></div>;
}

export function ModalObal({ title, icon, children, onClose }) {
  useEffect(() => { const h = (e) => { if (e.key === "Escape") onClose(); }; window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h); }, [onClose]);
  return (
    <div className="nad-klavesnici fixed inset-0 bg-brand-dark/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl max-w-lg w-full max-h-[94vh] sm:max-h-[92vh] overflow-y-auto overscroll-contain" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 sm:p-5 border-b border-slate-200 flex items-center gap-3 bg-brand-50 sticky top-0 z-10">
          <div className="w-10 h-10 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center">{icon}</div>
          <div className="text-base font-black text-slate-900 flex-1">{title}</div>
          <button onClick={onClose} className="w-10 h-10 md:w-8 md:h-8 rounded-xl hover:bg-white/70 active:bg-white text-slate-500 flex items-center justify-center" aria-label="Zavřít"><X className="w-5 h-5 md:w-4 md:h-4" /></button>
        </div>
        <div className="p-4 sm:p-5 space-y-4">{children}</div>
      </div>
    </div>
  );
}

// ─── panel: pravidelné termíny ───────────────────────────────────────────────
function SeriePanel({ data, trener, onChange, setConfirm, onNova }) {
  const serie = data?.serie || [];
  const { data: navrhyData, refetch } = useApiData(`/api/rezervace?akce=navrhy_serii&trener=${encodeURIComponent(trener)}`, { bezKese: true });
  const navrhy = navrhyData?.navrhy || [];
  const [busyKey, setBusyKey] = useState("");
  const ukoncit = (s) => setConfirm({
    title: "Ukončit pravidelný termín?", description: `${s.klient_name}, ${DNY_DLOUHE[s.dow].toLowerCase()} ${s.cas}. Budoucí vygenerované tréninky od dneška se smažou, minulé zůstanou.`, confirmLabel: "Ukončit", variant: "danger",
    onConfirm: async () => { try { const r = await post({ akce: "serie_ukoncit", serie_id: s.id }); toast(`Pravidelný termín ukončen, smazáno ${r.smazano} budoucích tréninků.`, "success"); onChange(); refetch(); } catch (e) { toast(e.message, "error"); } },
  });
  const zalozit = async (n) => {
    setBusyKey(`${n.klient_id}|${n.dow}|${n.cas}`);
    try {
      const r = await post({ akce: "serie_vytvorit", trener, klient_id: n.klient_id, dow: n.dow, cas: n.cas, lokalita: n.lokalita, platnost_od: isoOf(pragueDate()) });
      toast(`Série založena, doplněno ${r.vytvoreno} tréninků${r.preskoceno?.length ? `, ${r.preskoceno.length} přeskočeno (kolize nebo firemní volno)` : ""}.`, "success"); onChange(); refetch();
    } catch (e) { toast(e.message, "error"); } finally { setBusyKey(""); }
  };
  return (
    <div className="px-4 sm:px-5 py-3 border-b border-slate-100 bg-slate-50/60 space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm font-bold text-slate-800">Pravidelné termíny</div>
        <button onClick={onNova} className="inline-flex items-center gap-1 text-xs font-bold text-brand-700 hover:underline"><Plus className="w-3.5 h-3.5" /> Nový pravidelný termín</button>
      </div>
      {serie.length === 0 && <div className="text-xs text-slate-400">Zatím žádný pravidelný termín. Založíš ho v nové rezervaci zaškrtnutím „Opakovat každý týden".</div>}
      {serie.length > 0 && (
        <div className="grid sm:grid-cols-2 gap-2">
          {serie.map((s) => (
            <div key={s.id} className="flex items-center justify-between gap-2 bg-white border border-slate-200 rounded-xl px-3 py-2 text-sm">
              <div className="min-w-0"><div className="font-bold text-slate-800 truncate demo-sensitive">{s.klient_name}</div><div className="text-xs text-slate-500">{DNY_DLOUHE[s.dow]} {s.cas}, {nazevLokality(data, s.lokalita)}{s.platnost_do ? `, do ${fmtCzRok(s.platnost_do)}` : ""}</div></div>
              <button onClick={() => ukoncit(s)} className="text-xs font-semibold text-rose-700 hover:underline flex-shrink-0">Ukončit</button>
            </div>
          ))}
        </div>
      )}
      {navrhy.length > 0 && (
        <div>
          <div className="text-xs font-bold text-slate-700 mb-1">Návrhy: tyhle termíny se opakují, ale nemají sérii. Bez ní se v dalších týdnech nezobrazí.</div>
          <div className="grid sm:grid-cols-2 gap-2">
            {navrhy.map((n) => { const key = `${n.klient_id}|${n.dow}|${n.cas}`; return (
              <div key={key} className="flex items-center justify-between gap-2 bg-white border border-dashed border-brand-300 rounded-xl px-3 py-2 text-sm">
                <div className="min-w-0"><div className="font-bold text-slate-800 truncate demo-sensitive">{n.klient_name}</div><div className="text-xs text-slate-500">{DNY_DLOUHE[n.dow]} {n.cas}, {n.terminy.length}× (naposledy {fmtCz(n.posledni)})</div></div>
                <button onClick={() => zalozit(n)} disabled={busyKey === key || !n.klient_id} title={!n.klient_id ? "Klientka není spárovaná, založ sérii ručně" : ""} className="text-xs font-bold text-brand-700 hover:underline disabled:opacity-50 flex-shrink-0">{busyKey === key ? "Zakládám" : "Založit sérii"}</button>
              </div>
            ); })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── panel: hodiny pro klientky (dostupnost v portálu) ───────────────────────
function L_lokalita(data) { return data?.trener?.lokalita || (lokalityZ(data)[0] || {}).kod || "VT"; }
function HodinyPanel({ data, trener, onChange, smiEditovat }) {
  const [okna, setOkna] = useState(() => (data?.dostupnost || []).map((o) => ({ ...o })));
  useEffect(() => { setOkna((data?.dostupnost || []).map((o) => ({ ...o }))); }, [data?.dostupnost]);
  const [busy, setBusy] = useState(false);
  // 1. 10. 2026: pobočka je dána trenérem (trainers.lokalita), v oknech se nevybírá.
  const lokalitaTrenera = L_lokalita(data);
  const pridat = () => setOkna((o) => [...o, { dow: 1, od_hod: 8, do_hod: 20, lokalita: lokalitaTrenera }]);
  const uprav = (i, k, v) => setOkna((o) => o.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const smaz = (i) => setOkna((o) => o.filter((_, idx) => idx !== i));
  const ulozit = async () => { setBusy(true); try { await post({ akce: "dostupnost", trener, okna: okna.map((o) => ({ ...o, lokalita: o.lokalita || lokalitaTrenera })) }); toast("Hodiny uloženy.", "success"); onChange(); } catch (e) { toast(e.message, "error"); } finally { setBusy(false); } };
  const zeVzoru = async () => {
    setBusy(true);
    try {
      const r = await apiFetch(`/api/rezervace?akce=dostupnost_navrh&trener=${encodeURIComponent(trener)}`); const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Chyba"); setOkna(j.okna || []); toast(`Předvyplněno ze vzorového rozvrhu: ${(j.okna || []).length} oken. Zkontroluj a ulož.`, "success");
    } catch (e) { toast(e.message, "error"); } finally { setBusy(false); }
  };
  const hod = []; for (let h = 6; h <= 22; h++) hod.push(h);
  if (!smiEditovat) {
    return (
      <div className="px-4 sm:px-5 py-3 border-b border-slate-100 bg-slate-50/60 space-y-2">
        <div className="text-sm font-bold text-slate-800">Tvoje pracovní okna</div>
        <div className="text-xs text-slate-500">{okna[0]?.zdroj === "vzor" ? "Berou se ze vzorového rozvrhu (schválené absence a volno se odečítají)." : "Nastavuje je manažer."} Klientky si v aplikaci vybírají z volných hodin v nich, ty můžeš zadat trénink i mimo ně.</div>
        {okna.length === 0 && <div className="text-xs text-amber-700">Zatím nic: ve vzorovém rozvrhu nemáš hodiny a manažer okna nenastavil. Klientky si tak samy nic nerezervují.</div>}
        <div className="flex flex-wrap gap-1.5">
          {okna.map((o, i) => <span key={i} className="text-xs bg-white border border-slate-200 rounded-xl px-2 py-1">{DNY_DLOUHE[o.dow]} {hh(o.od_hod)} až {hh(o.do_hod)}</span>)}
        </div>
      </div>
    );
  }
  return (
    <div className="px-4 sm:px-5 py-3 border-b border-slate-100 bg-slate-50/60 space-y-2">
      <div className="text-sm font-bold text-slate-800">Pracovní okna trenéra (hodiny pro klientky)</div>
      <div className="text-xs text-slate-500">Výchozí okna jsou hodiny ze vzorového rozvrhu (schválené absence a volno se odečítají samy). Ručně nastavená okna mají přednost. Klientky si v aplikaci vybírají z volných celých hodin v nich, trenér může zadat trénink i mimo ně.</div>
      {okna.map((o, i) => (
        <div key={i} className="flex flex-wrap items-center gap-2 text-sm">
          <select value={o.dow} onChange={(e) => uprav(i, "dow", Number(e.target.value))} className="field w-auto">{[1, 2, 3, 4, 5, 6, 7].map((d) => <option key={d} value={d}>{DNY_DLOUHE[d]}</option>)}</select>
          <select value={o.od_hod} onChange={(e) => uprav(i, "od_hod", Number(e.target.value))} className="field w-auto">{hod.slice(0, -1).map((h) => <option key={h} value={h}>{hh(h)}</option>)}</select>
          <span className="text-slate-400">až</span>
          <select value={o.do_hod} onChange={(e) => uprav(i, "do_hod", Number(e.target.value))} className="field w-auto">{hod.slice(1).map((h) => <option key={h} value={h}>{hh(h)}</option>)}</select>
          <button onClick={() => smaz(i)} className="text-slate-400 hover:text-rose-600"><X className="w-4 h-4" /></button>
        </div>
      ))}
      <div className="flex flex-wrap gap-3 items-center">
        <button onClick={pridat} className="inline-flex items-center gap-1 text-xs font-bold text-brand-700 hover:underline"><Plus className="w-3.5 h-3.5" /> Přidat okno</button>
        <button onClick={zeVzoru} disabled={busy} className="inline-flex items-center gap-1 text-xs font-bold text-brand-700 hover:underline disabled:opacity-50"><Repeat className="w-3.5 h-3.5" /> Předvyplnit ze vzorového rozvrhu</button>
        <button onClick={ulozit} disabled={busy} className="btn btn-primary ml-auto">{busy ? "Ukládám" : "Uložit okna"}</button>
      </div>
    </div>
  );
}

// ─── panel: kalendář v telefonu ──────────────────────────────────────────────
// 25. 10. 2026 (Láďa): nahoře přímé propojení Google kalendáře (změny do minuty, zapisuje
// admin sám, _lib/gcalSync.cjs). Odběr přes adresu zůstává pro Apple a jako záloha.
// `nutri` (1. 10. 2026): texty pro nutriční (konzultace místo tréninků).
const PIL = "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold";
function kdyKratce(iso) {
  if (!iso) return "";
  const d = new Date(iso); if (isNaN(d.getTime())) return "";
  const hm = d.toLocaleTimeString("cs-CZ", { hour: "numeric", minute: "2-digit", timeZone: "Europe/Prague" });
  const den = d.toLocaleDateString("cs-CZ", { day: "numeric", month: "numeric", timeZone: "Europe/Prague" });
  const dnes = new Date().toLocaleDateString("cs-CZ", { day: "numeric", month: "numeric", timeZone: "Europe/Prague" });
  return den === dnes ? `dnes ${hm}` : `${den} ${hm}`;
}
function GooglePropojeni({ trener, nutri }) {
  const { data, refetch, error } = useApiData(`/api/gcal?akce=stav&trener=${encodeURIComponent(trener)}`, { bezKese: true });
  const [busy, setBusy] = useState("");
  const co = nutri ? "konzultace" : "tréninky";
  const zavolej = async (akce) => {
    const r = await apiFetch("/api/gcal", { method: "POST", body: JSON.stringify({ akce, trener, zpet: nutri ? "/nutri/konzultace" : window.location.pathname }) });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j.error || "Google teď neodpovídá, zkus to prosím za chvíli.");
    return j;
  };
  const pripojit = async () => {
    setBusy("pripojit");
    try { const j = await zavolej("pripojit"); window.location.href = j.url; }
    catch (e) { toast(e.message, "error"); setBusy(""); }
  };
  const sync = async () => {
    setBusy("sync");
    try { await zavolej("sync"); await refetch?.(); toast("Hotovo, zbytek případně doplní automatická kontrola do minuty.", "success"); }
    catch (e) { toast(e.message, "error"); } finally { setBusy(""); }
  };
  const odpojit = async () => {
    const ok = await confirmDialog({ title: "Odpojit Google kalendář?", description: `Kalendář FW40 tréninky se z Googlu smaže a admin do něj přestane zapisovat ${co}. Propojit ho můžeš kdykoli znovu.`, confirmLabel: "Odpojit", variant: "danger" });
    if (!ok) return;
    setBusy("odpojit");
    try { await zavolej("odpojit"); await refetch?.(); toast("Google kalendář odpojen.", "success"); }
    catch (e) { toast(e.message, "error"); } finally { setBusy(""); }
  };
  if (error) return <div className="rounded-2xl border border-slate-200 bg-white p-4 text-[13px] text-slate-500">Stav Google kalendáře se nepodařilo načíst. Zkus to prosím později.</div>;
  if (!data) return <div className="h-28 animate-pulse rounded-2xl border border-slate-200 bg-white" />;
  if (!data.nastaveno) return <NastaveniKlienta trener={trener} owner={!!data.owner} onHotovo={() => refetch?.()} />;
  const pripojeno = !!data.pripojeno;
  const chyba = data.stav === "chyba";
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start gap-3">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-brand-50 text-lg">📆</span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[15px] font-bold text-slate-900">Google kalendář</span>
            {pripojeno ? <span className={`${PIL} border-emerald-200 bg-emerald-50 text-emerald-700`}>Propojeno</span>
              : chyba ? <span className={`${PIL} border-amber-300 bg-amber-50 text-amber-800`}>Propoj znovu</span>
              : <span className={`${PIL} border-slate-200 bg-slate-50 text-slate-600`}>Nepropojeno</span>}
          </div>
          <p className="mt-1 text-[13px] leading-snug text-slate-600">
            {pripojeno
              ? `Nové, přesunuté i zrušené ${co} se do kalendáře FW40 tréninky propíšou do minuty.`
              : `${nutri ? "Konzultace" : "Tréninky"} se zapíšou do samostatného kalendáře FW40 tréninky ve tvém Googlu a každá změna se propíše do minuty. Do tvých ostatních kalendářů admin nevidí.`}
          </p>
          {pripojeno && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {data.email && <span className={`${PIL} border-slate-200 bg-slate-50 text-slate-700`}>{data.email}</span>}
              <span className={`${PIL} border-brand-200 bg-brand-50 text-brand-700`}>{data.pocet || 0} v kalendáři</span>
              {data.posledni_sync_at && <span className={`${PIL} border-slate-200 bg-white text-slate-500`}>Kontrola {kdyKratce(data.posledni_sync_at)}</span>}
            </div>
          )}
          {data.chyba && <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-800">{data.chyba}</div>}
        </div>
      </div>
      <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        {!pripojeno && data.vlastni && (
          <button type="button" onClick={pripojit} disabled={!!busy} className="btn btn-primary min-h-[44px] w-full justify-center sm:w-auto">
            {busy === "pripojit" ? "Otevírám Google…" : chyba ? "Propojit znovu" : "Propojit Google kalendář"}
          </button>
        )}
        {!pripojeno && !data.vlastni && <p className="text-xs text-slate-500">Propojení si dělá každý trenér sám ve svém Google účtu.</p>}
        {pripojeno && (
          <>
            <button type="button" onClick={sync} disabled={!!busy} className="btn btn-secondary min-h-[44px] w-full justify-center sm:w-auto">{busy === "sync" ? "Kontroluji…" : "Zkontrolovat teď"}</button>
            <button type="button" onClick={odpojit} disabled={!!busy} className="btn min-h-[44px] w-full justify-center border border-slate-200 bg-white text-rose-700 hover:bg-rose-50 sm:w-auto">Odpojit</button>
          </>
        )}
      </div>
      {pripojeno && <p className="mt-3 text-xs text-slate-500">Máš v Googlu ještě starý odběr „FW40 tréninky {trener}“? Odeber ho, jinak uvidíš {co} dvakrát.</p>}
    </div>
  );
}

// Návrat z Google souhlasu (?gcal=…): hláška a vyčištění adresy. Vrací true, když se má otevřít panel.
const GCAL_HLASKY = {
  ok: ["Google kalendář je propojený, termíny se do něj zapisují.", "success"],
  zamitnuto: ["Propojení bylo v Googlu zrušené, nic se nezměnilo.", "info"],
  jiny_ucet: ["Propoj prosím stejný Google účet, kterým se přihlašuješ do adminu.", "error"],
  bez_kalendare: ["Google nepovolil přístup ke kalendáři. Zkus to znovu a nech zaškrtnutý kalendář.", "error"],
  neplatny_odkaz: ["Odkaz na propojení vypršel, zkus to prosím znovu.", "error"],
  chyba: ["Propojení se nepovedlo, zkus to prosím znovu.", "error"],
};
export function zpracujNavratGcal() {
  try {
    const u = new URL(window.location.href);
    const v = u.searchParams.get("gcal");
    if (!v) return false;
    const [text, typ] = GCAL_HLASKY[v] || GCAL_HLASKY.chyba;
    toast(text, typ);
    u.searchParams.delete("gcal");
    window.history.replaceState(null, "", u.pathname + (u.search ? u.search : "") + u.hash);
    return true;
  } catch { return false; }
}

// Klíč Google projektu FW40 Kalendář vkládá jen owner (uloží se šifrovaně, env už přidat nejde).
function NastaveniKlienta({ trener, owner, onHotovo }) {
  const [id, setId] = useState("");
  const [secret, setSecret] = useState("");
  const [busy, setBusy] = useState(false);
  if (!owner) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-4 text-[13px] text-slate-600">
        <div className="mb-1 text-[15px] font-bold text-slate-900">Google kalendář</div>
        Přímé propojení s Google kalendářem právě zapínáme. Zatím použij odběr níže.
      </div>
    );
  }
  const uloz = async () => {
    setBusy(true);
    try {
      const r = await apiFetch("/api/gcal", { method: "POST", body: JSON.stringify({ akce: "nastav_klienta", trener, client_id: id.trim(), client_secret: secret.trim() }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || `Chyba ${r.status}`);
      setSecret(""); toast("Uloženo, propojení je připravené.", "success"); onHotovo();
    } catch (e) { toast(e.message, "error"); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2 rounded-2xl border border-amber-200 bg-amber-50/60 p-4">
      <div className="text-[15px] font-bold text-slate-900">Nastavení Google kalendáře (jen owner)</div>
      <p className="text-[13px] text-slate-600">Vlož Client ID a Client secret z Google projektu FW40 Kalendář. Uloží se šifrovaně, nikde se nezobrazí.</p>
      <input className="field w-full" placeholder="Client ID (…apps.googleusercontent.com)" value={id} onChange={(e) => setId(e.target.value)} autoComplete="off" />
      <input className="field w-full" type="password" placeholder="Client secret" value={secret} onChange={(e) => setSecret(e.target.value)} autoComplete="new-password" />
      <button type="button" onClick={uloz} disabled={busy || !id.trim() || !secret.trim()} className="btn btn-primary min-h-[44px] w-full justify-center sm:w-auto">{busy ? "Ukládám…" : "Uložit"}</button>
    </div>
  );
}

function IcalPanel({ trener, nutri = false }) {
  const { data } = useApiData(`/api/rezervace?akce=ical_url&trener=${encodeURIComponent(trener)}`, { bezKese: true });
  const [ok, setOk] = useState(false);
  const [odber, setOdber] = useState(false);
  const url = data?.url || "";
  const webcal = url.replace(/^https:/, "webcal:");
  const google = url ? `https://calendar.google.com/calendar/render?cid=${encodeURIComponent(webcal)}` : "";
  const copy = async () => { try { await navigator.clipboard.writeText(url); setOk(true); setTimeout(() => setOk(false), 1500); } catch { toast("Zkopíruj adresu ručně.", "error"); } };
  // 30. 9. 2026: přidání jedním klepnutím (Google otevře odběr z adresy, Apple systémový dialog), adresa ke zkopírování zůstává.
  const Tl = ({ href, ikona, titulek, popis }) => (
    <a href={href || "#"} target={href && href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" aria-disabled={!href} className={`flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 min-h-[52px] text-left hover:bg-brand-50 active:bg-brand-100 ${!href ? "opacity-50 pointer-events-none" : ""}`}>
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-slate-100 text-base">{ikona}</span>
      <span className="min-w-0"><span className="block text-sm font-bold text-slate-800">{titulek}</span><span className="block text-xs text-slate-500">{popis}</span></span>
    </a>
  );
  return (
    <div className="px-4 sm:px-5 py-3 border-b border-slate-100 bg-slate-50/60 space-y-3 text-sm">
      <GooglePropojeni trener={trener} nutri={nutri} />
      <div className="rounded-2xl border border-slate-200 bg-white">
        <button type="button" onClick={() => setOdber((x) => !x)} aria-expanded={odber}
          className="flex min-h-[48px] w-full items-center gap-3 px-4 py-2.5 text-left">
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-slate-100 text-base">🍎</span>
          <span className="min-w-0 flex-1"><span className="block text-sm font-bold text-slate-800">Apple kalendář a odběr přes adresu</span><span className="block text-xs text-slate-500">iPhone se obnovuje obvykle do hodiny</span></span>
          <ChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition ${odber ? "rotate-180" : ""}`} />
        </button>
        {odber && (
          <div className="space-y-2 border-t border-slate-100 px-4 py-3">
            <div className="text-xs text-slate-500">Klepnutím se kalendář přidá jako odběr. Nové, přesunuté i zrušené {nutri ? "konzultace" : "tréninky"} se samy aktualizují (iPhone podle nastavení, obvykle do hodiny, Google odběr jen zhruba jednou za den, proto je lepší propojení nahoře). Adresu nikomu nedávej, je jako heslo.</div>
            <div className="grid gap-2 sm:grid-cols-2">
              <Tl href={url ? webcal : ""} ikona="🍎" titulek="Přidat do Apple kalendáře" popis="iPhone a Mac, otevře dialog odběru" />
              <Tl href={google} ikona="🔗" titulek="Odběr v Google kalendáři" popis="Pomalejší, změny až za několik hodin" />
            </div>
            <div className="flex gap-2 items-center">
              <input readOnly value={url} className="field flex-1" onFocus={(e) => e.target.select()} />
              <button onClick={copy} disabled={!url} className="btn btn-primary">{ok ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} Kopírovat</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
